<script>
  // No `location` import here: svelte-spa-router exports a derived STORE by
  // that name, and importing it shadows window.location at module scope — the
  // crash-recovery button below called location.reload() and threw.
  import Router, { replace } from 'svelte-spa-router';
  import { wrap } from 'svelte-spa-router/wrap';
  import { canWrite, isAdminOnly } from './lib/guards.js';
  import { onMount } from 'svelte';
  import { authStore, checkAuthStatus } from './stores/auth.js';
  import { setUserRoles } from './stores/roles.js';
  import { t } from './i18n/index.js';

  import Dashboard from './routes/Dashboard.svelte';
  import Invoices from './routes/Invoices.svelte';
  import InvoiceDetail from './routes/InvoiceDetail.svelte';
  import Scadenzario from './routes/Scadenzario.svelte';
  import Bilancio from './routes/Bilancio.svelte';
  import BilancioDetail from './routes/BilancioDetail.svelte';
  import Diagnostics from './routes/Diagnostics.svelte';
  import Login from './routes/Login.svelte';
  import ChangePassword from './routes/ChangePassword.svelte';
  import Companies from './routes/Companies.svelte';
  import CompanySettings from './routes/CompanySettings.svelte';
  import Customers from './routes/Customers.svelte';
  import InvoiceCreate from './routes/InvoiceCreate.svelte';
  import AutofatturaCreate from './routes/AutofatturaCreate.svelte';
  import Drafts from './routes/Drafts.svelte';
  import DraftEdit from './routes/DraftEdit.svelte';
  import Settings from './routes/Settings.svelte';
  import Commercialista from './routes/Commercialista.svelte';
  import NotFound from './routes/NotFound.svelte';
  import Brand from './components/Brand.svelte';
  import PageLoader from './components/PageLoader.svelte';
  import { loadCompanies } from './stores/company.js';
  import { loadPreferences } from './stores/preferences.js';
  import { createPreferencesGate } from './lib/preferences-gate.js';

  // The standalone /invoices/import, /anagrafica/import and /payment-methods
  // routes are gone — they now live as tabs under /settings/* and are
  // reached exclusively from the Impostazioni nav entry.
  //
  // Hiding a link is not a guard: every route below was reachable by typing its
  // hash, so a read-only role could open a creation form the server would
  // refuse on save. The write and administration routes now carry a condition;
  // the reading ones deliberately do not, because a commercialista is entitled
  // to read the books of the companies they are paired with.
  const routes = {
    '/': Dashboard,
    '/invoices': Invoices,
    '/invoices/new': wrap({ component: InvoiceCreate, conditions: [canWrite] }),
    '/autofatture/new': wrap({ component: AutofatturaCreate, conditions: [canWrite] }),
    '/invoices/:id': InvoiceDetail,
    '/drafts': wrap({ component: Drafts, conditions: [canWrite] }),
    '/drafts/:id': wrap({ component: DraftEdit, conditions: [canWrite] }),
    '/scadenzario': Scadenzario,
    // Every company role reads the bilancio; the page itself hides upload,
    // replace and delete from the roles the server would refuse.
    '/bilancio': Bilancio,
    '/bilancio/:id': BilancioDetail,
    '/diagnostics': wrap({ component: Diagnostics, conditions: [isAdminOnly] }),
    '/change-password': ChangePassword,
    '/companies': wrap({ component: Companies, conditions: [isAdminOnly] }),
    '/companies/:id/settings': wrap({ component: CompanySettings, conditions: [isAdminOnly] }),
    '/customers': Customers,
    '/settings': Settings,
    '/settings/:tab': Settings,
    '/commercialista': Commercialista,
    // Catch-all: any unknown hash route renders the in-shell 404 page.
    '*': NotFound,
  };

  let isAuthenticated = false;
  let loading = true;
  let serverDown = false;

  /**
   * The signed-in shell waits for the account's appearance preferences, up to
   * PREFERENCES_WAIT_MS, so that someone who turned on Dettagli inWD on
   * another device does not see the plain shell and then watch it change. A
   * slower answer is still applied when it arrives (and saved to this
   * browser, so the next load paints it straight away); an unreachable server
   * only costs the wait.
   */
  const PREFERENCES_WAIT_MS = 1500;
  let preferencesSettled = false;
  // One hold per signed-in user (lib/preferences-gate.js): a switch from one
  // account to another starts a new hold that only the new account's load,
  // or the timeout, can release.
  const preferencesGate = createPreferencesGate({
    load: loadPreferences,
    waitMs: PREFERENCES_WAIT_MS,
    onChange: (value) => { preferencesSettled = value; },
  });

  onMount(async () => {
    await checkAuthStatus();
  });

  // React to auth state changes
  $: {
    isAuthenticated = $authStore.isAuthenticated;
    loading = $authStore.loading;
    serverDown = $authStore.serverDown;
    if ($authStore.user?.roles) {
      setUserRoles($authStore.user.roles);
    }
    if ($authStore.isAuthenticated && !$authStore.loading) {
      loadCompanies();
    }
    preferencesGate.update({
      isAuthenticated: $authStore.isAuthenticated,
      loading: $authStore.loading,
      userId: $authStore.user?.id ?? null,
    });
  }

  function retryAuth() {
    checkAuthStatus();
  }
</script>

{#if loading || (isAuthenticated && !preferencesSettled)}
  <div class="login-screen">
    <div class="flex flex-col items-center gap-6 text-center">
      <Brand />
      <PageLoader label={$t('app.verifyingAccess')} essential />
      <p class="text-sm text-body" aria-hidden="true">{$t('app.verifyingAccess')}</p>
    </div>
  </div>
{:else if serverDown}
  <div class="login-screen">
    <div class="card p-8 w-full max-w-md text-center" role="alert">
      <div class="flex justify-center mb-6"><Brand /></div>
      <h1 class="text-lg text-heading font-display mb-2">{$t('app.serviceUnavailableTitle')}</h1>
      <p class="text-sm text-muted mb-6">{$t('app.serviceUnavailableBody')}</p>
      <button class="btn btn-primary w-full" on:click={retryAuth}>{$t('app.retry')}</button>
    </div>
  </div>
{:else if !isAuthenticated}
  <Login />
{:else}
  <svelte:boundary>
    <!-- A refused route goes back to the dashboard rather than to the 404
         page: the address exists, this account may not open it, and telling
         someone their own application does not exist is a worse answer. -->
    <Router {routes} on:conditionsFailed={() => replace('/')} />

    {#snippet failed(error, reset)}
      <div class="login-screen">
        <div class="card p-8 w-full max-w-md text-center" role="alert">
          <div class="flex justify-center mb-6"><Brand /></div>
          <h1 class="text-lg text-heading font-display mb-2">{$t('app.crashTitle')}</h1>
          <p class="text-sm text-muted mb-6">{$t('app.crashBody')}</p>
          <div class="flex gap-3 justify-center">
            <button class="btn btn-secondary" on:click={reset}>{$t('app.retry')}</button>
            <button class="btn btn-primary" on:click={() => window.location.reload()}>{$t('app.reload')}</button>
          </div>
        </div>
      </div>
    {/snippet}
  </svelte:boundary>
{/if}
